<script setup lang="ts">
// Floating-bar comparison of typical repair vs replacement cost ranges,
// themed from the --site-* tokens at mount time (canvas can't read var()).
import { onBeforeUnmount, onMounted, ref } from 'vue'
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    LinearScale,
    Tooltip,
    type ChartConfiguration,
} from 'chart.js'
import { chartTheme, withAlpha } from '@/shared/charts/theme'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip)

const canvas = ref<HTMLCanvasElement | null>(null)
let chart: Chart<'bar', [number, number][]> | null = null

onMounted(() => {
    if (!canvas.value) return
    const t = chartTheme()

    const config: ChartConfiguration<'bar', [number, number][]> = {
        type: 'bar',
        data: {
            labels: ['Common repairs', 'Major repairs', 'Full replacement'],
            datasets: [
                {
                    data: [
                        [150, 800],
                        [1200, 3000],
                        [6000, 12000],
                    ],
                    backgroundColor: [
                        withAlpha(t.accent, 0.75),
                        withAlpha(t.warning, 0.75),
                        withAlpha(t.danger, 0.8),
                    ],
                    borderColor: [t.accent, t.warning, t.danger],
                    borderWidth: 2,
                    borderRadius: 4,
                    barPercentage: 0.55,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const [low, high] = ctx.raw as [number, number]
                            return ` $${low.toLocaleString()} – $${high.toLocaleString()}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: t.textStrong, font: { family: t.fontBody, size: 12 } },
                },
                y: {
                    grid: { color: withAlpha(t.grid, 0.6) },
                    ticks: {
                        color: t.text,
                        font: { family: t.fontMono, size: 11 },
                        callback: (v) => `$${Number(v).toLocaleString()}`,
                    },
                },
            },
        },
    }
    chart = new Chart(canvas.value, config)
})

onBeforeUnmount(() => chart?.destroy())
</script>

<template>
    <div class="cost-chart">
        <canvas ref="canvas" aria-label="Typical repair vs replacement cost ranges"></canvas>
    </div>
</template>

<style scoped lang="scss">
.cost-chart {
    position: relative;
    height: 280px;
}
</style>
